import { Component, OnInit, Inject, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Renderer2, ViewChild, HostListener, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, NavigationEnd, RouterOutlet, RouterModule } from '@angular/router';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType, SilentRequest, AccountInfo } from '@azure/msal-browser';
import { Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { PanelMenuModule } from 'primeng/panelmenu';
import { ButtonModule } from 'primeng/button';
import { TreeModule } from 'primeng/tree';
import { SidebarComponent } from "./sidebar/sidebar.component";
import { DarkModeService } from './dark-mode.service';
import { Title } from '@angular/platform-browser';
import { ToolbarModule } from 'primeng/toolbar';
import { MyMonitoringService } from '../services/logging.service';
import { environment } from '../environments/environment';
import { TokenService } from '../services/token.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, FooterComponent, PanelMenuModule,
    ButtonModule, TreeModule, SidebarComponent, ToolbarModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],

})
export class AppComponent implements OnInit, OnDestroy {
  isDarkMode = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private unregisterActivityListeners: Array<() => void> = [];
  private readonly sessionTimeoutMs = (environment.appSettings.sessionTimeoutMinutes ?? 30) * 60 * 1000;
  menuItems: any = []; // Populate this with actual menu items
  isMinimized: boolean = false;
  treeNodes: any = [];
  isMobileView: boolean = false;
  isSidebarOpen: boolean = false;
  currentTitle: String = "";

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private darkModeService: DarkModeService, private renderer: Renderer2,
    private activatedRoute: ActivatedRoute, private router: Router,
    private titleService: Title,
    private monotoringService: MyMonitoringService,
    private tokenService: TokenService
  ) {

  }

  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('darkMode', this.isDarkMode.toString());  // Save the state
  }

  ngOnInit(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map(route => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      mergeMap(route => route.data)
    ).subscribe(event => {
    
           const pageTitle = document.querySelector('.page-title');
            if (pageTitle instanceof HTMLElement) {
              pageTitle.textContent = event['title'];
            }

    });

    this.darkModeService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode = isDarkMode;
    });
    const savedTheme = localStorage.getItem('darkMode');
    this.isDarkMode = savedTheme === 'true';  // Convert string to boolean
    this.startActivityTracking();
    this.authService.handleRedirectObservable().subscribe();
    this.setLoginDisplay();
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        if (this.loginDisplay) {
          this.tokenService.acquireTokenSilent().subscribe();
        }
      })

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      })
  }

  //  Function to toggle sidebar state
  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen; // Toggle the sidebar state
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    this.clearInactivityTimer();
    this.unregisterActivityListeners.forEach((unregister) => unregister());
    this.unregisterActivityListeners = [];
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    if (this.loginDisplay) {
      this.resetInactivityTimer();
      return;
    }

    this.clearInactivityTimer();
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();
    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
  }

  private startActivityTracking(): void {
    const userActivityEvents = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    this.unregisterActivityListeners = userActivityEvents.map((eventName) =>
      this.renderer.listen('window', eventName, () => {
        if (this.loginDisplay) {
          this.resetInactivityTimer();
        }
      })
    );
  }

  private resetInactivityTimer(): void {
    this.clearInactivityTimer();
    this.inactivityTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.sessionTimeoutMs);
  }

  private clearInactivityTimer(): void {
    if (!this.inactivityTimer) {
      return;
    }

    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = null;
  }

  private handleSessionTimeout(): void {
    this.clearInactivityTimer();
    window.sessionStorage.clear();
    this.authService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin
    }).subscribe();
  }
}

function Of(title: Observable<string | undefined>): String {
  throw new Error('Function not implemented.');
}

